import React, { useState } from 'react';
import { Card, CardContent, Typography, IconButton, Menu, MenuItem, Box, Chip, Checkbox } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IContest, TSubmissionCategoryAward } from '../../../../types/global/helper';
import { formatAwardPrizeForDisplay } from '../../../../pages/BrandDashboard/contestWizard/buildAwardCategoryPayload';
import {
    brandAwardBodyTextSx,
    brandAwardCardDividerSx,
    brandAwardCardHeadlineSx,
    brandAwardCardTextNoWrapSx,
    brandAwardCategoryCardSx,
    brandAwardCheckboxSx,
    brandAwardExtraPrizeRowSx,
    brandAwardPrizeAmountSx,
    brandAwardPrizeLabelSx,
    brandAwardTierBadgeSx,
    brandAwardTierBadgeTextSx,
    brandContestSecondaryIconButtonSx,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

function awardHeadline(award: TSubmissionCategoryAward, isTiered: boolean): string {
    if (isTiered && award.tier_position && !MONGO_OBJECT_ID_RE.test(award.tier_position)) {
        return award.tier_position;
    }
    return award.category_title || '';
}

/** Tier label for badge — tier_position or tier_name, without trailing "Winner". */
function awardTierLabel(award: TSubmissionCategoryAward): string | null {
    let raw = award.tier_position?.trim() || '';
    if (!raw || MONGO_OBJECT_ID_RE.test(raw)) {
        raw = award.tier_name?.trim() || '';
    }
    if (!raw) return null;
    return raw.replace(/\s+winner$/i, '').trim();
}

interface AwardCategoryCardProps {
    award: TSubmissionCategoryAward;
    onMoreAction: (action: string, awardId: string) => void;
    hasSubmissions: boolean;
    contest?: IContest;
    isSelected?: boolean;
    onSelect?: (awardId: string, selected: boolean) => void;
    visualVariant?: 'default' | 'brand';
}

const AwardCategoryCard: React.FC<AwardCategoryCardProps> = ({
    award,
    onMoreAction,
    hasSubmissions,
    contest,
    isSelected = false,
    onSelect,
    visualVariant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isTiered =
        (contest && contest.submission_mode === 'tier') ||
        (contest && contest.categories?.some(cat => cat.tier_fee && cat.tier_fee?.length > 0));

    const headline = awardHeadline(award, Boolean(isTiered));
    const tierLabel = isTiered ? awardTierLabel(award) : null;
    const prizeAmount = formatAwardPrizeForDisplay(award);
    const hasCashPrize = Boolean(prizeAmount);
    const hasAdditionalPrizes = Boolean(award.additional_prize?.length);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action: string) => {
        onMoreAction(action, award._id);
        handleMenuClose();
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onSelect) {
            onSelect(award._id, event.target.checked);
        }
    };

    const menuButton = (
        <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={
                isBrand
                    ? {
                          ...brandContestSecondaryIconButtonSx,
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          flexShrink: 0,
                      }
                    : { color: theme[themeStyle].primary_white }
            }>
            <MoreVertIcon />
        </IconButton>
    );

    const actionMenu = (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: isBrand ? midnight.surfaceHighest : theme[themeStyle].table_header,
                    border: isBrand ? '1px solid rgba(255,255,255,0.08)' : undefined,
                },
            }}>
            {!hasSubmissions ? (
                <MenuItem
                    onClick={() => handleAction('edit')}
                    sx={{ color: isBrand ? midnight.onSurface : theme[themeStyle].primary_white }}>
                    Edit
                </MenuItem>
            ) : null}
            {!hasSubmissions ? (
                <MenuItem
                    onClick={() => handleAction('duplicate')}
                    sx={{ color: isBrand ? midnight.onSurface : theme[themeStyle].primary_white }}>
                    Duplicate
                </MenuItem>
            ) : null}
            <MenuItem
                onClick={() => handleAction('delete')}
                sx={{ color: isBrand ? midnight.onSurface : theme[themeStyle].primary_white }}>
                Delete
            </MenuItem>
        </Menu>
    );

    if (isBrand) {
        return (
            <Card sx={brandAwardCategoryCardSx(isSelected)}>
                <CardContent
                    sx={{
                        flexGrow: 1,
                        p: { xs: 2, sm: 2.5 },
                        position: 'relative',
                        zIndex: 1,
                        '&:last-child': { pb: { xs: 2, sm: 2.5 } },
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 2.5,
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    minWidth: 0,
                                }}>
                                {!hasSubmissions && onSelect ? (
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={handleCheckboxChange}
                                        sx={{ ...brandAwardCheckboxSx, mt: 0 }}
                                    />
                                ) : null}
                                <Typography
                                    sx={{
                                        ...brandAwardCardHeadlineSx,
                                        flex: 1,
                                        minWidth: 0,
                                    }}>
                                    {headline}
                                </Typography>
                            </Box>
                            {tierLabel ? (
                                <Box
                                    sx={{
                                        ...brandAwardTierBadgeSx,
                                        maxWidth: '100%',
                                        mt: 0.75,
                                        ml: !hasSubmissions && onSelect ? 4.5 : 0,
                                    }}>
                                    <MilitaryTechOutlinedIcon
                                        sx={{ fontSize: 14, color: midnight.primary, flexShrink: 0 }}
                                    />
                                    <Typography component="span" sx={brandAwardTierBadgeTextSx}>
                                        Tier: {tierLabel}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                        <Box sx={{ flexShrink: 0, mt: 0 }}>{menuButton}</Box>
                    </Box>

                    <Box sx={{ mb: hasAdditionalPrizes ? 0 : 0.5 }}>
                        {hasCashPrize ? (
                            <>
                                <Typography sx={brandAwardPrizeLabelSx}>Prize</Typography>
                                <Typography sx={brandAwardPrizeAmountSx}>{prizeAmount}</Typography>
                            </>
                        ) : (
                            <Typography sx={{ ...brandAwardBodyTextSx, mb: 1 }}>
                                {hasAdditionalPrizes && !isTiered ? '' : 'No prize'}
                            </Typography>
                        )}
                    </Box>

                    {hasAdditionalPrizes ? (
                        <>
                            {hasCashPrize ? <Box sx={brandAwardCardDividerSx} /> : null}
                            <Typography
                                sx={{
                                    ...brandAwardPrizeLabelSx,
                                    color: midnight.outline,
                                    mb: 1.5,
                                }}>
                                Additional Prizes
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {award.additional_prize!.map((prize: string, index: number) => (
                                    <Box key={index} sx={brandAwardExtraPrizeRowSx}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '8px',
                                                bgcolor: 'rgba(255, 181, 71, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                            <WorkspacePremiumOutlinedIcon
                                                sx={{ fontSize: 20, color: midnight.primary }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={
                                                [
                                                    brandAwardBodyTextSx,
                                                    brandAwardCardTextNoWrapSx,
                                                    {
                                                        fontSize: 13,
                                                        color: midnight.onSurface,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        ml: 0,
                                                    },
                                                ] as SxProps<Theme>
                                            }>
                                            {prize}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    ) : null}

                    {award.sub_category ? (
                        <Chip
                            label={award.sub_category.charAt(0).toUpperCase() + award.sub_category.slice(1)}
                            sx={{
                                mt: 2,
                                bgcolor: 'rgba(255, 201, 101, 0.14)',
                                color: midnight.primary,
                                border: '1px solid rgba(255,201,101,0.28)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.7rem',
                            }}
                        />
                    ) : null}
                </CardContent>
                {actionMenu}
            </Card>
        );
    }

    return (
        <Card
            sx={{
                backgroundColor: theme[themeStyle].secondaryBg,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: isSelected ? `2px solid ${theme[themeStyle].highlight}` : 'none',
            }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    {!hasSubmissions && onSelect && (
                        <Checkbox
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            sx={{
                                color: theme[themeStyle].primary_white,
                                '&.Mui-checked': { color: theme[themeStyle].highlight },
                                mr: 1,
                            }}
                        />
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].primary_white,
                            }}>
                            {headline}
                        </Typography>
                        {isTiered && tierLabel && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].primary_orange,
                                    fontSize: '0.875rem',
                                    mt: 0.5,
                                }}>
                                Tier: {tierLabel}
                            </Typography>
                        )}
                    </Box>
                    {menuButton}
                </Box>

                <Box sx={{ mb: 2 }}>
                    {hasCashPrize ? (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].primary_white,
                                mb: 1,
                            }}>
                            Prize: {formatAwardPrizeForDisplay(award) || '—'}
                        </Typography>
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].primary_white,
                                mb: 1,
                            }}>
                            {hasAdditionalPrizes && !isTiered ? '' : 'No prize'}
                        </Typography>
                    )}

                    {hasAdditionalPrizes && (
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].primary_white,
                                    mb: 0.5,
                                }}>
                                Additional Prizes:
                            </Typography>
                            {award.additional_prize!.map((prize: string, index: number) => (
                                <Typography
                                    key={index}
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                        ml: 2,
                                    }}>
                                    • {prize}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {award.sub_category && (
                        <Chip
                            label={award.sub_category.charAt(0).toUpperCase() + award.sub_category.slice(1)}
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    )}
                </Box>
            </CardContent>
            {actionMenu}
        </Card>
    );
};

export default AwardCategoryCard;
